import { useState } from 'react';
import { LineChart } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApiError } from '@/lib/api/client';
import { toast } from '@/hooks/useToast';
import { useUpdateDispensingSettings } from '@/features/pharmacy/useNearExpiry';
import type { DispensingSettingsAdmin } from '@/features/pharmacy/types';

/**
 * Reorder planning (Phase 1 G16) — the days behind Pharmacy → Stock insights'
 * suggested order: enough for the delivery time, until the next order, plus a
 * safety margin, at the store's recent daily use.
 */
export function ReorderPlanningCard({ settings }: { settings: DispensingSettingsAdmin }) {
  const update = useUpdateDispensingSettings();
  const init = { lead: String(settings.reorder_lead_days ?? 7), review: String(settings.reorder_review_days ?? 7), safety: String(settings.reorder_safety_days ?? 3) };
  const [v, setV] = useState(init);
  const [error, setError] = useState<string | null>(null);
  const dirty = v.lead !== init.lead || v.review !== init.review || v.safety !== init.safety;

  const save = () => {
    const n = { lead: Number(v.lead), review: Number(v.review), safety: Number(v.safety) };
    if (![n.lead, n.review, n.safety].every((x) => Number.isInteger(x) && x >= 0 && x <= 180) || n.review < 1) {
      setError('Use whole numbers of days from 0 to 180 (the review period at least 1)'); return;
    }
    setError(null);
    update.mutate({ reorder_lead_days: n.lead, reorder_review_days: n.review, reorder_safety_days: n.safety }, {
      onSuccess: () => toast({ title: 'Reorder planning saved', variant: 'success' }),
      onError: (e) => setError(e instanceof ApiError ? e.message : 'Not saved'),
    });
  };

  const field = (key: keyof typeof v, label: string) => (
    <label className="flex flex-col gap-1 text-[11.5px] text-ink-muted">
      {label}
      <Input type="number" min={0} max={180} className="h-8 w-24 tabular-nums" value={v[key]} onChange={(e) => setV({ ...v, [key]: e.target.value })} />
    </label>
  );
  return (
    <Card>
      <CardHeader><CardTitle><LineChart size={15} /> Reorder planning</CardTitle></CardHeader>
      <p className="mb-3 text-[11.5px] leading-snug text-ink-muted">
        Pharmacy → Stock insights suggests ordering enough for the delivery time, the days until the next order, and a
        safety margin, at each store’s recent daily use — less what is on hand, on the way or already ordered.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        {field('lead', 'Delivery time (days)')}
        {field('review', 'Days between orders')}
        {field('safety', 'Safety margin (days)')}
        <Button size="sm" disabled={!dirty} loading={update.isPending} onClick={save}>Save</Button>
      </div>
      {error && <div className="mt-2 text-xs text-danger-ink">{error}</div>}
    </Card>
  );
}
